/**
 * Touch controls -> BlobInput.
 *
 * Left thumb: one wide pad. Which half of it the thumb is on decides left or
 * right, and sliding across the middle switches direction without lifting,
 * which is far easier to hit blind than two separate buttons. Right thumb:
 * jump and smash. Nothing here is a gesture -- a game where the smash costs
 * energy needs buttons you can hold, not swipes you have to complete.
 *
 * The controls are DOM elements rather than hit-tested canvas regions, so the
 * browser does the multi-touch bookkeeping and each finger is tracked by its
 * own pointer id. A pointer is captured on the way down, which means a thumb
 * that drifts off a button keeps holding it: dropping your movement mid-rally
 * because your thumb slid two millimetres would be miserable.
 */
import type { BlobInput } from '@office/sim';

export type TouchAction = 'left' | 'right' | 'jump' | 'attack';

/**
 * Vertical space the control clusters occupy, in CSS pixels.
 *
 * The renderer reserves this so the court sits entirely above the buttons. It
 * costs court width, and it is worth it: the blobs live on the floor, which is
 * exactly where the thumbs are, and a control that covers the thing you are
 * controlling is a bad control.
 */
export const TOUCH_BAND_PX = 80;

/**
 * Which way a thumb at `x` on a pad spanning [`left`, `left + width`) means.
 * No dead zone: a thumb resting on the middle still has to pick a side, and
 * flipping exactly at the centre is what makes the pad feel like a rocker.
 */
export function padDirection(x: number, left: number, width: number): 'left' | 'right' {
  return x - left < width / 2 ? 'left' : 'right';
}

/**
 * The held-button bookkeeping, with no DOM in sight. One pointer holds one
 * action; several pointers can hold different ones at the same time.
 */
export class TouchInputState {
  private readonly byPointer = new Map<number, TouchAction>();

  press(pointerId: number, action: TouchAction): void {
    this.byPointer.set(pointerId, action);
  }

  release(pointerId: number): void {
    this.byPointer.delete(pointerId);
  }

  /** Whether this pointer is currently holding anything. */
  has(pointerId: number): boolean {
    return this.byPointer.has(pointerId);
  }

  /** Drop everything: used when the page is hidden or the match ends. */
  clear(): void {
    this.byPointer.clear();
  }

  get activeCount(): number {
    return this.byPointer.size;
  }

  isHeld(action: TouchAction): boolean {
    for (const held of this.byPointer.values()) {
      if (held === action) return true;
    }
    return false;
  }

  read(): BlobInput {
    return {
      left: this.isHeld('left'),
      right: this.isHeld('right'),
      jump: this.isHeld('jump'),
      attack: this.isHeld('attack'),
    };
  }
}

/** True on a device whose primary pointer is a finger. */
export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer: coarse)').matches === true || navigator.maxTouchPoints > 0)
  );
}

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

/**
 * Whether this browser can go fullscreen at all. iPhone Safari cannot, for
 * anything but a video, so there the button is not offered rather than offered
 * and dead.
 */
export function fullscreenSupported(doc: Document = document): boolean {
  const root = doc.documentElement as FullscreenElement;
  if ('fullscreenEnabled' in doc && doc.fullscreenEnabled === false) return false;
  return typeof root.requestFullscreen === 'function' || typeof root.webkitRequestFullscreen === 'function';
}

function toggleFullscreen(doc: Document = document): void {
  const d = doc as FullscreenDocument;
  const root = doc.documentElement as FullscreenElement;
  const active = d.fullscreenElement ?? d.webkitFullscreenElement;
  try {
    if (active) {
      void (d.exitFullscreen?.() ?? d.webkitExitFullscreen?.());
    } else {
      void (root.requestFullscreen?.() ?? root.webkitRequestFullscreen?.());
    }
  } catch {
    // Some browsers throw synchronously; the button simply does nothing.
  }
}

const STYLE = `
.touch {
  --touch-fg: #e6ebf5;
  --touch-surface: rgba(255, 255, 255, 0.07);
  --touch-held: rgba(255, 255, 255, 0.22);
  --touch-accent: #4fd1c5;
  --touch-energy: #ffd25f;
  position: fixed;
  inset: 0;
  z-index: 30;
  pointer-events: none;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  font: 600 13px/1 system-ui, -apple-system, "Segoe UI", sans-serif;
}
.touch[hidden] { display: none !important; }
.touch .cluster {
  position: absolute;
  bottom: max(10px, env(safe-area-inset-bottom));
  display: flex;
  gap: 12px;
  align-items: flex-end;
  pointer-events: none;
}
.touch .cluster.move { left: max(16px, env(safe-area-inset-left)); }
.touch .cluster.act { right: max(16px, env(safe-area-inset-right)); }
.touch button {
  pointer-events: auto;
  touch-action: none;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: var(--touch-surface);
  color: var(--touch-fg);
  font: inherit;
  display: grid;
  place-content: center;
  transition: background 80ms ease, transform 80ms ease;
}
.touch button:focus-visible { outline: 2px solid var(--touch-accent); outline-offset: 2px; }
.touch button .glyph { font-size: 20px; line-height: 1; opacity: 0.85; }

/* Move pad: one pill, two halves. The held half lights up. */
.touch .pad {
  width: 156px;
  height: 64px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  place-content: stretch;
  overflow: hidden;
}
.touch .pad span {
  display: grid;
  place-content: center;
  font-size: 18px;
  opacity: 0.7;
  transition: background 80ms ease, opacity 80ms ease;
}
.touch .pad[data-held="left"] span:first-child,
.touch .pad[data-held="right"] span:last-child {
  background: var(--touch-held);
  opacity: 1;
}

.touch .round { width: 64px; height: 64px; }
.touch .round[data-held="true"] { background: var(--touch-held); transform: scale(0.94); }

/* Smash doubles as the energy bar: a ring that empties as the bar drains. */
.touch .attack {
  --energy: 1;
  position: relative;
  color: var(--touch-energy);
}
.touch .attack::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 3px;
  background: conic-gradient(var(--touch-energy) calc(var(--energy) * 360deg), transparent 0);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.9;
  pointer-events: none;
}
.touch .attack[data-held="true"] { background: rgba(255, 210, 95, 0.28); }

.touch .corner {
  position: absolute;
  top: max(10px, env(safe-area-inset-top));
  right: max(12px, env(safe-area-inset-right));
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-size: 16px;
  opacity: 0.7;
}

/* A 16:9 court in portrait is a letterbox strip. Ask for the phone sideways. */
.rotate {
  position: fixed;
  inset: 0;
  z-index: 45;
  display: none;
  place-content: center;
  text-align: center;
  padding: 24px;
  background: #0b0f18;
  color: #9fb3d1;
  font: 15px/1.6 system-ui, -apple-system, "Segoe UI", sans-serif;
}
.rotate .icon {
  font-size: 40px;
  display: block;
  margin-bottom: 12px;
  animation: rotate-hint 2.4s ease-in-out infinite;
}
@keyframes rotate-hint {
  0%, 40% { transform: rotate(0deg); }
  60%, 100% { transform: rotate(90deg); }
}
@media (orientation: portrait) and (pointer: coarse) {
  .rotate { display: grid; }
}
@media (prefers-reduced-motion: reduce) {
  .rotate .icon { animation: none; }
  .touch button, .touch .pad span { transition: none; }
}
`;

export interface TouchControlsOptions {
  /** Show a fullscreen toggle where the browser supports one. */
  fullscreenButton?: boolean;
}

export class TouchControls {
  readonly element: HTMLElement;
  private readonly state = new TouchInputState();
  private readonly rotateHint: HTMLElement;
  private readonly pad: HTMLButtonElement;
  private readonly buttons = new Map<TouchAction, HTMLButtonElement>();
  private readonly attackButton: HTMLButtonElement;
  private readonly cleanups: (() => void)[] = [];

  constructor(opts: TouchControlsOptions = {}) {
    if (!document.getElementById('touch-style')) {
      const style = document.createElement('style');
      style.id = 'touch-style';
      style.textContent = STYLE;
      document.head.append(style);
    }

    this.element = document.createElement('div');
    this.element.className = 'touch';
    this.element.dataset['touch'] = 'controls';

    const move = document.createElement('div');
    move.className = 'cluster move';
    const act = document.createElement('div');
    act.className = 'cluster act';

    this.pad = this.buildPad();
    move.append(this.pad);

    this.attackButton = this.buildRound('attack', 'smash', '⚡︎');
    act.append(this.attackButton, this.buildRound('jump', 'jump', '↑'));

    this.element.append(move, act);

    if (opts.fullscreenButton !== false && fullscreenSupported()) {
      this.element.append(this.buildFullscreenButton());
    }

    this.rotateHint = document.createElement('div');
    this.rotateHint.className = 'rotate';
    this.rotateHint.innerHTML =
      '<div><span class="icon">📱</span>Turn your phone sideways.<br>The court is wider than it is tall.</div>';

    document.body.append(this.element, this.rotateHint);

    // A backgrounded page never sends pointerup, so buttons would stick.
    const onHide = (): void => {
      if (document.hidden) this.releaseAll();
    };
    document.addEventListener('visibilitychange', onHide);
    this.cleanups.push(() => document.removeEventListener('visibilitychange', onHide));

    // Safety net for a finger that lifts somewhere else entirely: without
    // capture the button would never hear about it and would stay held.
    const onGlobalUp = (event: PointerEvent): void => {
      this.state.release(event.pointerId);
      this.syncHeld();
    };
    window.addEventListener('pointerup', onGlobalUp);
    window.addEventListener('pointercancel', onGlobalUp);
    this.cleanups.push(() => {
      window.removeEventListener('pointerup', onGlobalUp);
      window.removeEventListener('pointercancel', onGlobalUp);
    });
  }

  /** Paint every control from the state, which is the only source of truth. */
  private syncHeld(): void {
    for (const [action, button] of this.buttons) {
      button.dataset['held'] = String(this.state.isHeld(action));
    }
    this.pad.dataset['held'] = this.state.isHeld('left')
      ? 'left'
      : this.state.isHeld('right')
        ? 'right'
        : 'false';
  }

  private capture(button: HTMLButtonElement, event: PointerEvent): void {
    // Capture is an enhancement -- it keeps a drifting thumb on the button --
    // and it throws for a pointer the browser does not consider active, which
    // must never cost you the input.
    try {
      button.setPointerCapture(event.pointerId);
    } catch {
      // Uncaptured: the window-level release is the safety net.
    }
  }

  private buildPad(): HTMLButtonElement {
    const pad = document.createElement('button');
    pad.type = 'button';
    pad.className = 'pad';
    pad.dataset['action'] = 'move';
    pad.dataset['held'] = 'false';
    pad.setAttribute('aria-label', 'move left or right');
    const l = document.createElement('span');
    l.textContent = '◀';
    const r = document.createElement('span');
    r.textContent = '▶';
    pad.append(l, r);

    const aim = (event: PointerEvent): void => {
      const rect = pad.getBoundingClientRect();
      this.state.press(event.pointerId, padDirection(event.clientX, rect.left, rect.width));
      this.syncHeld();
    };
    const down = (event: PointerEvent): void => {
      event.preventDefault();
      aim(event);
      this.capture(pad, event);
    };
    const move = (event: PointerEvent): void => {
      // Only a finger that started on the pad steers it; a stray hover does not.
      if (!this.state.has(event.pointerId)) return;
      aim(event);
    };
    const up = (event: PointerEvent): void => {
      event.preventDefault();
      this.state.release(event.pointerId);
      this.syncHeld();
    };
    pad.addEventListener('pointerdown', down);
    pad.addEventListener('pointermove', move);
    pad.addEventListener('pointerup', up);
    pad.addEventListener('pointercancel', up);
    pad.addEventListener('contextmenu', (e) => e.preventDefault());
    this.cleanups.push(() => {
      pad.removeEventListener('pointerdown', down);
      pad.removeEventListener('pointermove', move);
      pad.removeEventListener('pointerup', up);
      pad.removeEventListener('pointercancel', up);
    });
    return pad;
  }

  private buildRound(action: TouchAction, label: string, glyph: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `round ${action}`;
    button.dataset['action'] = action;
    button.dataset['held'] = 'false';
    button.setAttribute('aria-label', label);
    const g = document.createElement('span');
    g.className = 'glyph';
    g.textContent = glyph;
    button.append(g);

    const down = (event: PointerEvent): void => {
      event.preventDefault();
      this.state.press(event.pointerId, action);
      this.syncHeld();
      this.capture(button, event);
    };
    const up = (event: PointerEvent): void => {
      event.preventDefault();
      this.state.release(event.pointerId);
      this.syncHeld();
    };
    button.addEventListener('pointerdown', down);
    button.addEventListener('pointerup', up);
    button.addEventListener('pointercancel', up);
    button.addEventListener('contextmenu', (e) => e.preventDefault());
    this.cleanups.push(() => {
      button.removeEventListener('pointerdown', down);
      button.removeEventListener('pointerup', up);
      button.removeEventListener('pointercancel', up);
    });
    this.buttons.set(action, button);
    return button;
  }

  private buildFullscreenButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'corner';
    button.setAttribute('aria-label', 'toggle fullscreen');
    button.textContent = '⛶';
    button.addEventListener('click', () => toggleFullscreen());
    return button;
  }

  /** Show how much smash energy is left, 0..1, on the smash button's ring. */
  setEnergy(fraction: number): void {
    const v = Math.min(1, Math.max(0, fraction));
    this.attackButton.style.setProperty('--energy', v.toFixed(3));
  }

  read(): BlobInput {
    return this.state.read();
  }

  releaseAll(): void {
    this.state.clear();
    this.syncHeld();
  }

  setVisible(visible: boolean): void {
    this.element.hidden = !visible;
    if (!visible) this.releaseAll();
  }

  destroy(): void {
    for (const cleanup of this.cleanups) cleanup();
    this.element.remove();
    this.rotateHint.remove();
  }
}
